import React, { useState, useCallback } from "react";
import toast from "react-hot-toast";
import RegistrationCheckForm from "../components/home/FormTemplate";
import { useApiMutation } from "../hooks/useMutation";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate } from "react-router-dom";
import Button from "../utils/Button";
import { ArrowBack } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";

interface FormData {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  phone?: string;
  email: string;
  dob: string;
  gender: string;
  reminder: boolean;
}

interface RegistrationResponse {
  registration: {
    id: number;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zipcode: string;
    phone: string;
    email: string;
    dob: string;
    gender: string;
    reminder: boolean;
    isRegistered: boolean;
    actorId: number;
    actorName: string;
    partnerId: number;
    partnerName: string;
    shiftId: number | null;
    createdAt: string;
    updatedAt: string;
  };
  redirectUrl: string;
  onlineVoteByMailAllowed: boolean;
  voteByMailUrl: string;
}

export default function Vbm() {
  const [showResults, setShowResults] = useState(false);
  const [showVBMCompletionModal, setShowVBMCompletionModal] = useState(false);
  const [showVBMOptIn, setShowVBMOptIn] = useState(false);
  const [hasRequestedBallot, setHasRequestedBallot] = useState(false);
  const [registrationData, setRegistrationData] = useState<RegistrationResponse | null>(null);
  const [vbmOptIns, setVbmOptIns] = useState({
    mailBallotReminders: false,
    ballotTracking: false,
    returnDeadlineReminders: false
  });
  const [searchParams] = useSearchParams();
  const isInIframe = window.self !== window.top;
  const embedIdFromPage = searchParams.get("embed_id") || window.sessionStorage.getItem("embedded_partner_id") || "";
  const isEmbeddedFlow = isInIframe || searchParams.get("embed") === "true" || !!embedIdFromPage;

  const navigate = useNavigate()

  const registrationCheckMutation = useApiMutation<RegistrationResponse>({
    onSuccess: (result) => {
      setRegistrationData(result);
      setShowResults(true);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Registration check failed. Please try again.';
      toast.error(errorMessage);
    },
  });

  const vbmOptInMutation = useApiMutation({
    onSuccess: (response: { message?: string }) => {
      console.log('VBM opt-in successful');
      toast.success(response?.message || 'VBM opt-in successful');
      navigate("/")
    },
    onError: (error: unknown) => {
      console.error('VBM opt-in error:', error);
      const errorMessage =
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'VBM opt-in failed';
      toast.error(errorMessage);
    },
  });

  const handleFormSubmit = useCallback((data: FormData) => {
    const trimmedPhone = data.phone?.trim();
    const submissionData = {
      url: "/registration-check",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(isEmbeddedFlow && embedIdFromPage ? { "x-embed-id": embedIdFromPage } : {}),
      },
      data: {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        address: data.address.trim(),
        city: data.city.trim(),
        state: data.state,
        zipcode: data.zipcode.trim(),
        ...(trimmedPhone ? { phone: trimmedPhone } : {}),
        email: data.email.trim(),
        dob: data.dob,
        gender: data.gender ? data.gender.toUpperCase() : "",
        reminder: data.reminder,
      },
    };

    registrationCheckMutation.mutate(submissionData);
  }, [registrationCheckMutation, isEmbeddedFlow, embedIdFromPage]);

  const handleRequestOnStateSite = useCallback(() => {
    if (registrationData?.voteByMailUrl) {
      window.open(registrationData.voteByMailUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
      // Show completion modal immediately after opening new tab
      setTimeout(() => {
        setShowVBMCompletionModal(true);
      }, 1000);
    }
  }, [registrationData]);

  const handleVBMCompletionResponse = useCallback((response: string) => {
    setShowVBMCompletionModal(false);

    // Make API call to update VBM status immediately
    const statusMap: Record<string, string> = {
      'requested': 'REQUESTED',
      'didnt_complete': 'NOT_COMPLETED',
      'not_eligible': 'NOT_ELIGIBLE'
    };

    const status = statusMap[response];

    const updateVBMStatus = () => {
      if (status && registrationData?.registration?.id) {
        const submissionData = {
          url: `/vote-by-mail/${registrationData.registration.id}`,
          method: 'POST',
          data: {
            status: status,
            signOnSource: 'WEB PORTAL'
          },
          headers: {
            "Content-Type": "application/json",
            ...(isEmbeddedFlow && embedIdFromPage ? { "x-embed-id": embedIdFromPage } : {}),
          },
        };
        vbmOptInMutation.mutate(submissionData);
      }
    };

    if (response === 'requested') {
      updateVBMStatus();
      setHasRequestedBallot(true);
      setShowVBMOptIn(true);
    } else if (response === 'didnt_complete') {
      updateVBMStatus();
      // Take them back to state site again
      setTimeout(() => {
        if (registrationData?.voteByMailUrl) {
          window.open(registrationData.voteByMailUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
          setTimeout(() => {
            setShowVBMCompletionModal(true);
          }, 1000);
        }
      }, 1000);
    } else if (response === 'not_eligible') {
      updateVBMStatus();
    }
  }, [registrationData, vbmOptInMutation, isEmbeddedFlow, embedIdFromPage]);

  const handleVBMOptInChange = useCallback((option: string, checked: boolean) => {
    setVbmOptIns(prev => ({
      ...prev,
      [option]: checked
    }));
  }, []);

  const handleVBMOptInSubmit = useCallback(() => {
    const enabled = [];
    if (vbmOptIns.mailBallotReminders) enabled.push('MAIL_BALLOT_REMINDERS');
    if (vbmOptIns.ballotTracking) enabled.push('MAIL_BALLOT_TRACKING_INFO');
    if (vbmOptIns.returnDeadlineReminders) enabled.push('MAIL_BALLOT_DEADLINE');

    if (enabled.length > 0) {
      const optInData = {
        url: `/vote-by-mail/${registrationData?.registration?.id}`,
        method: 'PUT',
        headers: {
          "Content-Type": "application/json",
          ...(isEmbeddedFlow && embedIdFromPage ? { "x-embed-id": embedIdFromPage } : {}),
        },
        data: {
          reminderOptions: enabled
        }
      };
      vbmOptInMutation.mutate(optInData);
    }
  }, [vbmOptIns, registrationData, vbmOptInMutation, isEmbeddedFlow, embedIdFromPage]);

  const handleBackToForm = () => {
    setShowResults(false);
    setShowVBMCompletionModal(false);
    setShowVBMOptIn(false);
    setHasRequestedBallot(false);
    setRegistrationData(null);
  };

  return (
    <div className="min-h-screen flex flex-col items-center pt-12 pb-12 px-2"
      style={{
        background: "linear-gradient(135deg, #e0e7ff 0%, #fafafa 80%)",
        minHeight: "100vh",
      }}
    >
      {isInIframe && <div className="flex items-center gap-2 cursor-pointer absolute top-5 left-5  text-sm" onClick={() => window.history.back()}>
        <ArrowBack /> Go back
      </div>}
      <div className="w-full max-w-4xl mx-auto">
        {!showResults ? (
          <div className="space-y-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Request Your Mail Ballot
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Enter your information to request a vote-by-mail ballot
              </p>
            </div>
            <RegistrationCheckForm
              header="Vote By Mail"
              onSubmit={handleFormSubmit}
              isLoading={registrationCheckMutation.isPending}
            />
          </div>
        ) : registrationData && !hasRequestedBallot && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl text-gray-900 font-semibold mb-4">Vote By Mail</h2>

            {registrationData.onlineVoteByMailAllowed ? (
              <>
                <p className="text-gray-600 mb-6">
                  You can request your mail ballot through your state's official website.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800 font-medium mb-2">State VBM Website:</p>
                  <p className="text-xs text-blue-600 break-all">{registrationData.voteByMailUrl}</p>
                </div>
                <Button
                  onClick={handleRequestOnStateSite}
                  variant="primary"
                  size="extra-large"
                  label="Request on State Site"
                />
              </>
            ) : (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                  <ErrorOutlineIcon className="w-12 h-12 text-red-600 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-red-900 mb-2">Not Available</h3>
                  <p className="text-red-800">
                    Sorry, vote by mail option is not allowed for your state.
                  </p>
                </div>
                <Button
                  onClick={handleBackToForm}
                  variant="secondary"
                  size="extra-large"
                  label="Back to Form"
                />
              </>
            )}
          </div>
        )}

        {/* VBM Opt-In Component */}
        {showVBMOptIn && registrationData && (
          <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
            <h2 className="text-2xl text-gray-900 font-semibold mb-4">Stay Updated on Your Mail Ballot</h2>
            <p className="text-gray-600 mb-6">
              Get reminders and tracking information about your vote-by-mail ballot throughout the election cycle.
            </p>

            <div className="space-y-4 mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={vbmOptIns.mailBallotReminders}
                  onChange={(e) => handleVBMOptInChange('mailBallotReminders', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Mail ballot reminders</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={vbmOptIns.ballotTracking}
                  onChange={(e) => handleVBMOptInChange('ballotTracking', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Ballot tracking information</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={vbmOptIns.returnDeadlineReminders}
                  onChange={(e) => handleVBMOptInChange('returnDeadlineReminders', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Return deadline reminders</span>
              </label>
            </div>

            <div className="flex mx-auto w-fit gap-3">
              <Button
                onClick={handleVBMOptInSubmit}
                variant="primary"
                size="large"
                label="Subscribe"
              />
              <Button
                onClick={handleBackToForm}
                variant="secondary"
                size="large"
                label="Skip"
              />
            </div>
          </div>
        )}

        {/* VBM Completion Modal */}
        {showVBMCompletionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-slate-800 mb-4">What happened on the state website?</h2>
                <p className="text-gray-600 mb-6">
                  Did you successfully request your mail ballot?
                </p>

                <div className="space-y-3 mb-6">
                  <button
                    onClick={() => handleVBMCompletionResponse('requested')}
                    className="w-full px-4 py-3 border border-slate-300 text-black rounded-lg hover:bg-gray-100 transition-colors text-left flex items-center gap-3"
                  >
                    <CheckCircleIcon />
                    I requested a mail ballot
                  </button>
                  <button
                    onClick={() => handleVBMCompletionResponse('didnt_complete')}
                    className="w-full px-4 py-3  border border-slate-300 text-gray-800 rounded-lg hover:bg-gray-100 transition-colors text-left flex items-center gap-3"
                  >
                    <ErrorOutlineIcon />
                    I didn't complete it
                  </button>
                </div>

                <button
                  onClick={handleBackToForm}
                  className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
